"use client";

import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { X, Download, ExternalLink } from "lucide-react";
import type { Resource } from "./ResourceList";

interface ResourceViewerModalProps {
    resource: Resource | null;
    isOpen: boolean;
    onClose: () => void;
}

function getOfficeViewerUrl(url: string) {
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
}

function isOfficeFile(mimeType: string | null) {
    if (!mimeType) return false;
    return (
        mimeType.includes("word") ||
        mimeType.includes("powerpoint") ||
        mimeType.includes("presentation") ||
        mimeType.includes("excel") ||
        mimeType.includes("spreadsheet") ||
        mimeType === "application/msword" ||
        mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        mimeType === "application/vnd.ms-powerpoint" ||
        mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        mimeType === "application/vnd.ms-excel" ||
        mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
}

function FileViewer({ resource }: { resource: Resource }) {
    const { content, mime_type, file_name } = resource;

    if (mime_type?.startsWith("image/")) {
        return (
            <div className="flex flex-1 min-h-0 items-center justify-center bg-slate-50 p-6 overflow-auto">
                <img
                    src={content}
                    alt={file_name ?? resource.title}
                    className="max-w-full max-h-full rounded-lg object-contain shadow"
                />
            </div>
        );
    }

    if (mime_type === "application/pdf") {
        return (
            <iframe
                src={content}
                className="flex-1 w-full"
                title={resource.title}
            />
        );
    }

    if (isOfficeFile(mime_type)) {
        return (
            <iframe
                src={getOfficeViewerUrl(content)}
                className="flex-1 w-full"
                title={resource.title}
            />
        );
    }

    // Unsupported file type — show download prompt
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-slate-500">
            <p className="text-sm">This file type can't be previewed.</p>
            <a
                href={content}
                download={file_name ?? true}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
                <Download className="h-4 w-4" />
                Download file
            </a>
        </div>
    );
}

function LinkViewer({ resource }: { resource: Resource }) {
    useEffect(() => {
        if (resource.embeddable === false) {
            window.open(resource.content, "_blank", "noopener,noreferrer");
        }
    }, [resource.content, resource.embeddable]);

    if (resource.embeddable === false) {
        return (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 text-slate-500">
                <p className="text-sm">Opening externally…</p>
                <a
                    href={resource.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Click here if it didn't open
                </a>
            </div>
        );
    }

    return (
        <div className="flex flex-1 flex-col min-h-0">
            <iframe
                src={resource.content}
                className="flex-1 w-full min-h-0"
                title={resource.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
            />
            <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-2.5 flex-shrink-0">
                <p className="text-xs text-slate-400">Having trouble viewing? Open it in a new tab.</p>
                <a
                    href={resource.content}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open externally
                </a>
            </div>
        </div>
    );
}

export default function ResourceViewerModal({ resource, isOpen, onClose }: ResourceViewerModalProps) {
    if (!resource) return null;

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-900 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="flex flex-col w-full max-w-5xl h-[85vh] rounded-xl bg-white shadow-xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3 flex-shrink-0">
                                <Dialog.Title className="text-sm font-semibold text-slate-900 truncate pr-4">
                                    {resource.title}
                                </Dialog.Title>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {resource.type === "FILE" && (
                                        <a
                                            href={resource.content}
                                            download={resource.file_name ?? true}
                                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            Download
                                        </a>
                                    )}
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="rounded-md p-1 text-slate-400 hover:text-slate-600"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Viewer */}
                            {resource.type === "FILE" && <FileViewer resource={resource} />}
                            {resource.type === "LINK" && <LinkViewer resource={resource} />}
                            {resource.type === "RICH_TEXT" && (
                                <div
                                    className="prose prose-sm max-w-none flex-1 overflow-y-auto px-6 py-5 text-slate-700"
                                    dangerouslySetInnerHTML={{ __html: resource.content }}
                                />
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
